import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * LayoutModule component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconLayoutModule extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M3.20814 1.10498C2.04686 1.10498 1.10547 2.04638 1.10547 3.20765V4.56031C1.10547 5.72159 2.04686 6.66298 3.20814 6.66298H4.5608C5.72207 6.66298 6.66347 5.72159 6.66347 4.56031V3.20765C6.66347 2.04638 5.72207 1.10498 4.5608 1.10498H3.20814ZM11.3241 1.10498C10.1628 1.10498 9.22144 2.04638 9.22144 3.20765V4.56031C9.22144 5.72159 10.1628 6.66298 11.3241 6.66298H12.6768C13.838 6.66298 14.7794 5.72159 14.7794 4.56031V3.20765C14.7794 2.04638 13.838 1.10498 12.6768 1.10498H11.3241ZM19.4401 1.10498C18.2788 1.10498 17.3374 2.04638 17.3374 3.20765V4.56031C17.3374 5.72159 18.2788 6.66298 19.4401 6.66298H20.7927C21.954 6.66298 22.8954 5.72159 22.8954 4.56031V3.20765C22.8954 2.04638 21.954 1.10498 20.7927 1.10498H19.4401ZM3.20814 9.22021C2.04686 9.22021 1.10547 10.1616 1.10547 11.3229V12.6755C1.10547 13.8368 2.04686 14.7782 3.20814 14.7782H4.5608C5.72207 14.7782 6.66347 13.8368 6.66347 12.6755V11.3229C6.66347 10.1616 5.72207 9.22021 4.5608 9.22021H3.20814ZM11.3241 9.22021C10.1628 9.22021 9.22144 10.1616 9.22144 11.3229V12.6755C9.22144 13.8368 10.1628 14.7782 11.3241 14.7782H12.6768C13.838 14.7782 14.7794 13.8368 14.7794 12.6755V11.3229C14.7794 10.1616 13.838 9.22021 12.6768 9.22021H11.3241ZM19.4401 9.22021C18.2788 9.22021 17.3374 10.1616 17.3374 11.3229V12.6755C17.3374 13.8368 18.2788 14.7782 19.4401 14.7782H20.7927C21.954 14.7782 22.8954 13.8368 22.8954 12.6755V11.3229C22.8954 10.1616 21.954 9.22021 20.7927 9.22021H19.4401ZM3.20814 17.3374C2.04686 17.3374 1.10547 18.2788 1.10547 19.4401V20.7927C1.10547 21.954 2.04686 22.8954 3.20814 22.8954H4.5608C5.72207 22.8954 6.66347 21.954 6.66347 20.7927V19.4401C6.66347 18.2788 5.72207 17.3374 4.5608 17.3374H3.20814ZM11.3241 17.3374C10.1628 17.3374 9.22144 18.2788 9.22144 19.4401V20.7927C9.22144 21.954 10.1628 22.8954 11.3241 22.8954H12.6768C13.838 22.8954 14.7794 21.954 14.7794 20.7927V19.4401C14.7794 18.2788 13.838 17.3374 12.6768 17.3374H11.3241ZM19.4401 17.3374C18.2788 17.3374 17.3374 18.2788 17.3374 19.4401V20.7927C17.3374 21.954 18.2788 22.8954 19.4401 22.8954H20.7927C21.954 22.8954 22.8954 21.954 22.8954 20.7927V19.4401C22.8954 18.2788 21.954 17.3374 20.7927 17.3374H19.4401Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-layout-module') === undefined) {
  customElements.define('cat-icon-layout-module', BlocksIconLayoutModule);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-layout-module': BlocksIconLayoutModule;
  }
}
